import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../../cdl/Button/Button';
import { Checkbox } from '../../../cdl/Checkbox/Checkbox';
import { Link } from '../../../cdl/Link/Link';
import { Modal } from '../../../cdl/Modal/Modal';
import { Text } from '../../../cdl/Text/Text';
import { queries } from '../../../common/api/queryKeys/queries';
import { DataSheet } from '../../../common/DataSheet/DataSheet';
import { formatDateDelivery } from '../../../common/helpers/formatDateDelivery.helper';
import { formatPort } from '../../../common/helpers/formatPort.helper';
import { formatTotalPrice } from '../../../common/helpers/formatTotal.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { Box } from '../../../common/ui/Box';
import { usePortDetails } from '../../hooks/usePortDetails';
import { FormikCreateEnquiryValues } from '../CreateEnquiryFlow';
import { useSupplierSelectionPrices } from '../hooks/useSupplierSelectionPrices';

import { SupplierSelectionPriceState } from './SupplierSelection.types';

interface DirectOrderModalProps {
    values: FormikCreateEnquiryValues;
    isPending: boolean;
    onDismiss: () => void;
    onConfirm: () => void;
}

export const DirectOrderModal = ({ values, isPending, onDismiss, onConfirm }: DirectOrderModalProps) => {
    const vesselQuery = useQuery(queries.vessels.detail(values.vesselId));
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const portQuery = usePortDetails(values.portId);
    const supplier = values.assignedSuppliers.find((supplier) => supplier.id === values.receiverSupplierIds[0]);
    const activeItems = values.items.filter(
        (item) => !item.deleted && (item.productId || item.units || item.sourceValues?.product?.name?.trim())
    );
    const selectedContractId = values.receiverContractIds[0];
    const { pricesByContractId } = useSupplierSelectionPrices({
        suppliers: supplier ? [supplier] : [],
        items: activeItems,
        portId: values.portId,
        customerId: values.customerId,
        vesselId: values.vesselId,
        deliveryDate: values.dateDelivery ?? undefined,
        contractIdsBySupplierId: supplier && selectedContractId ? { [supplier.id]: [selectedContractId] } : {},
        selectedContractIdsBySupplierId: supplier && selectedContractId ? { [supplier.id]: selectedContractId } : {},
    });
    const price = selectedContractId ? pricesByContractId[selectedContractId] : undefined;
    const totalPrice =
        activeItems.length > 0 &&
        price?.state === SupplierSelectionPriceState.AVAILABLE &&
        price.items?.length === activeItems.length
            ? price.totalPrice
            : undefined;
    const showGtc = supplier?.showGtc;
    const gtcUrl = supplier?.supplierGroup.gtcUrl;

    return (
        <Modal isOpen onDismiss={onDismiss} dismissible={!isPending} scrollable={false}>
            <Box padding={24} display="grid" rowGap={16}>
                <Text variant="title">{translate('order.directOrder.confirmDirectOrder')}</Text>
                <Text variant="small" color="foreground.muted">
                    {translate('order.directOrder.pleaseReviewTheDetailsBelowAndConfirmYourDirectOrder')}
                </Text>
                <DataSheet>
                    <DataSheet.Label>{translate('offer.supplier')}</DataSheet.Label>
                    <Text>{supplier?.name}</Text>
                    <DataSheet.Label>{translate('global.coreData.vessel')}</DataSheet.Label>
                    <Text>{vesselQuery.data?.name}</Text>
                    <DataSheet.Label>{translate('global.coreData.port')}</DataSheet.Label>
                    <Text>{portQuery.data ? formatPort({ port: portQuery.data }) : '—'}</Text>
                    <DataSheet.Label>{translate('global.coreData.estimatedDelivery')}</DataSheet.Label>
                    <Text>{formatDateDelivery({ date: values.dateDelivery })}</Text>
                    <DataSheet.Label>{translate('order.detail.orderModal.total')}</DataSheet.Label>
                    <Text>{totalPrice ? formatTotalPrice(totalPrice) : Characters.EN_DASH}</Text>
                </DataSheet>
                {isPending ? (
                    <Text variant="small" color="foreground.muted">
                        {translate('order.directOrder.placingOrder')}
                    </Text>
                ) : null}
                {showGtc ? (
                    <Box display="flex" flexDirection="column" gap={16}>
                        <Text variant="small" color="foreground.muted">
                            {translate('order.directOrder.placingThisOrderImpliesAgreementToThe')}{' '}
                            <Link href={gtcUrl} target="_blank" size="small">
                                {translate('order.detail.orderModal.termsAndConditionsLink')}
                            </Link>{' '}
                            {translate('order.detail.orderModal.termsAndConditions2', { supplier: supplier.name })}
                        </Text>
                        <Checkbox
                            checked={acceptedTerms}
                            onChange={() => setAcceptedTerms(!acceptedTerms)}
                            disabled={isPending}
                            label={translate('order.detail.orderModal.acceptLabel')}
                        />
                    </Box>
                ) : null}
                <Box display="flex" justifyContent="end" columnGap={8} marginTop={8}>
                    <Button type="button" onClick={onDismiss} disabled={isPending}>
                        {translate('btn.cancel')}
                    </Button>
                    <Button
                        type="button"
                        emphasis="high"
                        isLoading={isPending}
                        disabled={
                            !supplier ||
                            !vesselQuery.data ||
                            !portQuery.data ||
                            !!(showGtc && (!acceptedTerms || !gtcUrl))
                        }
                        onClick={onConfirm}
                    >
                        {isPending
                            ? translate('order.directOrder.placingOrder')
                            : translate('order.directOrder.confirmDirectOrder')}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};
